/**
 * Global page headers: https://go.nuxtjs.dev/config-head
 */

import {MetaInfo} from 'vue-meta/types/vue-meta';
import {buildAppConfig} from './env';
import ChildView from '~/lib/types/ChildView';


type MetaPropertyHid = {
  hid: string
  property: string
  content: string
};

export type NuxtMetaInfo = Omit<MetaInfo, 'meta'> & {
  meta?: (Exclude<MetaInfo['meta'], undefined>[0] | MetaPropertyHid | null)[]
};


export function metaProperty(name: string, value: string): MetaPropertyHid {
  return {
    hid: name,
    property: name,
    content: value
  };
}


export function childHtmlHead(child: ChildView): MetaInfo {
  let siteName = metadata.title!.replace(/!$/, '');
  const title = `${siteName} - ${child.fullName}`;

  const image = child.photoThumbUrl || child.photoUrl
  const head: NuxtMetaInfo = {
    title: title,
    meta: [
        metaProperty('title', title),
        metaProperty('og:description', title),
        image ? metaProperty('og:image', child.photoUrl) : null,
        image ? metaProperty('og:image:width', '200') : null,
        image ? metaProperty('og:image:height', '200') : null,
        metaProperty('og:site_name', siteName),
    ].filter(x => !!x)
  };

  return head as MetaInfo;
}


const scripts = [];

if(buildAppConfig.config.cookiebot.enabled) {
  scripts.push({
    id: 'Cookiebot',
    src: 'https://consent.cookiebot.com/uc.js',
    'data-cbid': buildAppConfig.config.cookiebot.cbid,
    'data-blockingmode': 'auto',
    type: 'text/javascript',
  });
}

if(buildAppConfig.config.ipresso.enabled) {

  // Scripts have 'text/plain' and 'data-cookieconsent', so cookiebot loads them after user consent.
  // See https://www.cookiebot.com/en/manual-implementation for more info.

  if(buildAppConfig.env === 'test') {

    // DEV IPRESSO CODE
    // scripts.push({
    //   id: 'ipresso-dev',
    //   type: 'text/plain',
    //   'data-cookieconsent': 'preferences',
    //   async: true,
    //   innerHTML: `function __ipGetMonitCode(t){for(var e="__ipcan_nsssF5qua7BJ12uCFsfwCjrK5tVu6R8GvqNi0qgsXF0.=",n=document.cookie.split(";"),o="null",i=0;i<n.length;i++){var r;r="function"!=typeof String.prototype.trim?n[i].replace(/^\s+|\s+$/g,""):n[i].trim(),0==r.indexOf(e)&&(o=r.substring(e.length,r.length))}var a=document.getElementById("ipressoMonitStart");if(null!=a)try{document.getElementsByTagName("head")[0].removeChild(a)}catch(f){}var c=document.getElementsByTagName("script")[0],m=document.createElement("script");m.type="text/javascript",m.id="ipressoMonitStart",m.async=!0,t=t===!0?1:0,m.src="//kupujipomagaj-media.ipresso.com/monitoridentification/nsssF5qua7BJ12uCFsfwCjrK5tVu6R8GvqNi0qgsXF0./xT9LqL69W8-JeaiVrkhYPiRuvZbEiAcFjiVbFpbKgvk./monitoridentification.js?r="+encodeURIComponent(document.referrer)+"&s="+encodeURIComponent(document.location.href.replace(document.location.hash,""))+"&ti="+encodeURIComponent(document.title)+"&c="+o+"&z=6MtCa28wYQvn8Om8PCauvpDzqcNYeNmcKJHmjw4qhdE.&as="+t,c.parentNode.insertBefore(m,c)}function __ipStartMonitoring(t){"undefined"!=typeof __ipIntervalTu&&__ipIntervalTu&&clearInterval(__ipIntervalTu),__ipGetMonitCode(t)}__ipStartMonitoring(!0);`
    // });

  } else if(buildAppConfig.env === 'prod') {

    // PROD IPRESSO CODE
    scripts.push({
      id: 'ipresso-prod',
      type: 'text/plain',
      'data-cookieconsent': 'preferences',
      async: true,
      innerHTML: `function __ipGetMonitCode(t){for(var e="__ipcan_nsssF5qua7BJ12uCFsfwCjrK5tVu6R8GvqNi0qgsXF0.=",n=document.cookie.split(";"),o="null",i=0;i<n.length;i++){var r;r="function"!=typeof String.prototype.trim?n[i].replace(/^\s+|\s+$/g,""):n[i].trim(),0==r.indexOf(e)&&(o=r.substring(e.length,r.length))}var a=document.getElementById("ipressoMonitStart");if(null!=a)try{document.getElementsByTagName("head")[0].removeChild(a)}catch(f){}var c=document.getElementsByTagName("script")[0],m=document.createElement("script");m.type="text/javascript",m.id="ipressoMonitStart",m.async=!0,t=t===!0?1:0,m.src="//kupujipomagaj-media.ipresso.com/monitoridentification/nsssF5qua7BJ12uCFsfwCjrK5tVu6R8GvqNi0qgsXF0./JQekJcrSUfXPmawNevV0aYYbstVCFLdo1XhfqA1NJ_s./monitoridentification.js?r="+encodeURIComponent(document.referrer)+"&s="+encodeURIComponent(document.location.href.replace(document.location.hash,""))+"&ti="+encodeURIComponent(document.title)+"&c="+o+"&z=6MtCa28wYQvn8Om8PCauvpDzqcNYeNmcKJHmjw4qhdE.&as="+t,c.parentNode.insertBefore(m,c)}function __ipStartMonitoring(t){"undefined"!=typeof __ipIntervalTu&&__ipIntervalTu&&clearInterval(__ipIntervalTu),__ipGetMonitCode(t)}__ipStartMonitoring(!0);`
    });

  }
}


const metadata: MetaInfo = {
  title: 'Kupuj i Pomagaj!',
  htmlAttrs: {
    lang: 'pl'
  },
  meta: [
    {charset: 'utf-8'},
    {name: 'viewport', content: 'width=device-width, initial-scale=1'},
    {name: 'format-detection', content: 'telephone=no'},
    {charset: 'utf-8'},
    {name: 'viewport', content: 'width=device-width, initial-scale=1'},
    {
      hid: 'description',
      name: 'description',
      content:
        'Kupuj i Pomagaj! - Rób zakupy w internecie, płać tyle co zawsze i wspieraj osoby niepełnosprawne!'
    },
    {
      name: 'google-site-verification',
      content: 'LKF-ba_dRQc_eUwBQMVqwUMV6jaw5ezzNE2HfHeHhE0'
    },
    {
      hid: `og:image`,
      property: 'og:image',
      content: `${buildAppConfig.frontendUrl}/images/backgrounds/header-desktop.webp`
    },
    {
      hid: `og:image:width`,
      property: 'og:image:width',
      content: '200'
    },
    {
      hid: `og:image:height`,
      property: 'og:image:height',
      content: '200'
    },
    {
      hid: `og:description`,
      property: 'og:description',
      content:
        'Kupuj i Pomagaj! - Rób zakupy w internecie, płać tyle co zawsze i wspieraj osoby niepełnosprawne!'
    },
  ],
  link: [
    {rel: 'manifest', href: '/manifest.json'},
    {rel: 'icon', type: 'image/x-icon', href: '/favicon.ico'},
    {rel: 'icon', type: 'image/png', href: '/favicon.webp'},
  ],
  script: scripts,

  // Fixes 'SyntaxError: Unexpected token '&' (at (index):4:1387)'
  // I.e. we get &quot; instead of '"' in the script tags.
  //
  // E.g.:
  //
  //     <script data-n-head="1" id="iPresso1" type="text/javascript" async>function __ipGetMonitCode(t){for(var e=&quot;__ipcan
  //
  // See below for more info:
  // https://github.com/nuxt/nuxt/issues/452
  // https://github.com/nuxt/nuxt/issues/226
  __dangerouslyDisableSanitizers: ['script']
}

export default metadata;
